import React, { Fragment, useState, useEffect } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  RefreshCcwIcon,
} from "lucide-react";
import { NewUser, User } from "../../../../types";
import { getUsers, updateUser } from "../../../../service/apiService";
import { useUser } from "../../../../context/UserContext";
import FilePermissions from "./FilePermissions";

const roles = ["admin", "user"];

const userStatusOptions = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" }
];
interface UpdateUserProps {
  onUserChange: () => void;
}

const UpdateUser: React.FC<UpdateUserProps> = ({ onUserChange }) => {
  const { userId } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<NewUser | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [roleQuery, setRoleQuery] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const result = await getUsers();
        setUsers(result);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const availableUsers = users.filter((user) => user.id !== userId);
  
  const filteredUsers =
    userQuery === ""
      ? availableUsers
      : availableUsers.filter((user) =>
          user.id
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(userQuery.toLowerCase().replace(/\s+/g, ""))
        );

  const filteredRoles =
    roleQuery === ""
      ? roles
      : roles.filter((role) =>
          role
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(roleQuery.toLowerCase().replace(/\s+/g, ""))
        );

  useEffect(() => {
    validateForm();
  }, [selectedUser, roleQuery]);

  const validateForm = () => {
    if (!selectedUser || selectedUser.user_id === "") {
      setValidationError("Please select a user.");
      return false;
    }
    if (selectedUser && selectedUser.role === "") {
      setValidationError("Please select a role.");
      return false;
    }
    if (selectedUser && !selectedUser.user_status) {
      setValidationError("Please select a user status.");
      return false;
    }
    setValidationError(null);
    return true;
  };

  const resetForm = () => {
    setSelectedUser(null);
    setUserQuery("");
    setRoleQuery("");
    setValidationError(null);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(null);

    try {
      if (selectedUser) {
        await updateUser(selectedUser.user_id, selectedUser.role, selectedUser.user_status, selectedUser.permissions);
        setSubmitSuccess(true);
        const result = await getUsers();
        setUsers(result);
        resetForm(); // Clear the form after successful submission
        onUserChange(); // Trigger the user change callback
      }
    } catch (error) {
      console.error("Error updating user:", error);
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Update User
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-2 text-sm mx-2 p-4 rounded-lg bg-white">
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="flex flex-col">
            <label
              htmlFor="user"
              className="block text-sm font-medium text-gray-700"
            >
              Select User
            </label>
            <Combobox<User>
              value={
                selectedUser
                  ? availableUsers.find((user) => user.id === selectedUser.user_id)
                  : undefined
              }
              onChange={(user: User | null) =>
                user ? setSelectedUser({ 
                  user_id: user.id, 
                  role: user.role, 
                  user_status: user.user_status || 'enabled',
                  permissions: user.permissions
                }) : setSelectedUser(null)
              }
            >
              <div className="relative mt-1">
                <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
                  <ComboboxInput
                    className="w-full h-full border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none focus:border-violet-500 focus:ring focus:ring-violet-200 focus:ring-opacity-20"
                    displayValue={(user: User) => user?.id ?? ""}
                    onChange={(event) => setUserQuery(event.target.value)}
                  />
                  {isLoadingUsers && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-8">
                      <RefreshCcwIcon
                        className="animate-spin h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                  )}
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </ComboboxButton>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setUserQuery("")}
                >
                  <ComboboxOptions className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {availableUsers.length === 0 ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        No other users available to update.
                      </div>
                    ) : filteredUsers.length === 0 && userQuery !== "" ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        Nothing found.
                      </div>
                    ) : (
                      filteredUsers.map((user) => (
                        <ComboboxOption
                          key={user.id}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active
                                ? "bg-violet-600 text-white"
                                : "text-gray-900"
                            }`
                          }
                          value={user}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-medium" : "font-normal"
                                }`}
                              >
                                {user.id}
                              </span>
                              {selected ? (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? "text-white" : "text-violet-600"
                                  }`}
                                >
                                  <CheckIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ComboboxOption>
                      ))
                    )}
                  </ComboboxOptions>
                </Transition>
              </div>
            </Combobox>
            {validationError &&
              selectedUser &&
              validationError.includes("user") && (
                <span className="text-red-500 text-sm">{validationError}</span>
              )}
          </div>
          {selectedUser && (
            <div className="flex flex-col mt-4">
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700"
              >
                Select Role
              </label>
              <Combobox<string>
                value={selectedUser.role}
                onChange={(role: string) =>
                  setSelectedUser({ ...selectedUser, role })
                }
              >
                <div className="relative mt-1">
                  <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
                    <ComboboxInput
                      className="w-full h-full border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none focus:border-violet-500 focus:ring focus:ring-violet-200 focus:ring-opacity-20"
                      displayValue={(role: string) => role}
                      onChange={(event) => setRoleQuery(event.target.value)}
                    />
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </ComboboxButton>
                  </div>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setRoleQuery("")}
                  >
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                      {filteredRoles.length === 0 && roleQuery !== "" ? (
                        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                          Nothing found.
                        </div>
                      ) : (
                        filteredRoles.map((role) => (
                          <ComboboxOption
                            key={role}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                active
                                  ? "bg-violet-600 text-white"
                                  : "text-gray-900"
                              }`
                            }
                            value={role}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}
                                >
                                  {role}
                                </span>
                                {selected ? (
                                  <span
                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                      active ? "text-white" : "text-violet-600"
                                    }`}
                                  >
                                    <CheckIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxOptions>
                  </Transition>
                </div>
              </Combobox>
              {validationError &&
                selectedUser &&
                validationError.includes("role") && (
                  <span className="text-red-500 text-sm">
                    {validationError}
                  </span>
                )}
            </div>
          )}
         {selectedUser && (
           <div className="flex flex-col mt-4">
             <label
               htmlFor="userStatus"
               className="block text-sm font-medium text-gray-700"
            >
            User Status
           </label>
           <Combobox<string>
             value={selectedUser.user_status}
             onChange={(status: string) =>
             setSelectedUser({ ...selectedUser, user_status: status })
           }
         >
          <div className="relative mt-1">
            <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
              <ComboboxInput
                className="w-full h-full border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none focus:border-violet-500 focus:ring focus:ring-violet-200 focus:ring-opacity-20"
                displayValue={(status: string) => 
                   userStatusOptions.find(option => option.value === status)?.label || ""
                }
                onChange={(event) => {}} 
             />
             <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
               <ChevronDownIcon
                 className="h-5 w-5 text-gray-400"
                 aria-hidden="true"
             />
            </ComboboxButton>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
           >
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              {userStatusOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-violet-600 text-white" : "text-gray-900"
                   }`
                }
                value={option.value}
              >
                {({ selected, active }) => (
                  <>
                    <span
                      className={`block truncate ${
                        selected ? "font-medium" : "font-normal"
                      }`}
                    >
                      {option.label}
                    </span>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                          active ? "text-white" : "text-violet-600"
                        }`}
                      >
                        <CheckIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </span>
                    ) : null}
                  </>
                )}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
    {validationError &&
      selectedUser &&
      validationError.includes("status") && (
        <span className="text-red-500 text-sm">
          {validationError}
        </span>
    )}
  </div>
)}
          {selectedUser && (
            <div className="mt-4">
              <FilePermissions
                user={{
                  ...availableUsers.find((user) => user.id === selectedUser.user_id)!,
                  permissions: selectedUser.permissions
                }}
                onPermissionsChange={(permissions) =>
                  setSelectedUser({ ...selectedUser, permissions })
                }
                disabled={isSubmitting}
              />
            </div>
          )}
         <div className="flex flex-col sm:flex-row sm:items-center">
            <button
              type="submit"
              className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                isSubmitting || validationError !== null
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
              disabled={isSubmitting || validationError !== null}
            >
              {isSubmitting ? (
                <>
                  <RefreshCcwIcon
                    className="animate-spin mr-2 h-5 w-5 text-white"
                    aria-hidden="true"
                  />
                  Updating...
                </>
              ) : (
                "Update User"
              )}
            </button>
            {submitSuccess !== null && (
              <div className="mt-2 sm:mt-0 sm:ml-2 flex items-center">
                {submitSuccess ? (
                  <>
                    <CheckIcon className="h-6 w-6 text-green-500" />
                    <span className="ml-2 text-sm text-green-500">
                      {selectedUser?.user_id} successfully updated
                    </span>
                  </>
                ) : (
                  <>
                    <XIcon className="h-6 w-6 text-red-500" />
                    <span className="ml-2 text-sm text-red-500">
                      {selectedUser?.user_id} failed to update
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </form>
      </DisclosurePanel>
    </Disclosure>
  );
};

export default UpdateUser;
